import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, ChevronRight, Loader2, CheckCircle } from 'lucide-react';
import { partnerCasesAPI } from '@/lib/api';
import { toast } from 'sonner';

const STATUS_LABELS = {
  assigned: 'Assigned',
  financials_in_preparation: 'Financials in Prep',
  under_review: 'Under Review',
  sent_to_cs: 'Sent to CS',
  filing_in_progress: 'Filing in Progress',
  completed: 'Completed',
};

const STATUS_COLORS = {
  assigned: 'bg-blue-100 text-blue-700',
  financials_in_preparation: 'bg-amber-100 text-amber-700',
  under_review: 'bg-purple-100 text-purple-700',
  sent_to_cs: 'bg-sky-100 text-sky-700',
  filing_in_progress: 'bg-orange-100 text-orange-700',
  completed: 'bg-green-100 text-green-700',
};

function getAwaitingAction(c, profession) {
  if (profession === 'CA') return ['assigned', 'financials_in_preparation'].includes(c.status);
  return ['sent_to_cs', 'filing_in_progress'].includes(c.status);
}

export default function PartnerEngagementsPage() {
  const { partner } = useOutletContext();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    partnerCasesAPI.getMyCases()
      .then(res => setCases(res.data))
      .catch(() => toast.error('Failed to load engagements'))
      .finally(() => setLoading(false));
  }, []);

  const totalCases = cases.length;
  const inProgress = cases.filter(c => c.status !== 'completed').length;
  const awaitingAction = cases.filter(c => getAwaitingAction(c, partner.profession)).length;
  const completed = cases.filter(c => c.status === 'completed').length;

  const filteredCases = filter === 'all' ? cases
    : filter === 'in_progress' ? cases.filter(c => c.status !== 'completed')
    : filter === 'awaiting' ? cases.filter(c => getAwaitingAction(c, partner.profession))
    : cases.filter(c => c.status === 'completed');

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="partner-engagements">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-[#1e3a5f]">Active Engagements</h1>
        <p className="text-xs text-slate-500 mt-1">Cases assigned to you. Track scope of work, update task status, and monitor progress.</p>
      </div>

      {/* Summary Strip */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="border-l-4 border-l-[#1e3a5f]">
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase font-medium mb-1">Total Assigned</p>
            <p className="text-3xl font-bold text-[#1e3a5f]" data-testid="total-cases">{totalCases}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase font-medium mb-1">In Progress</p>
            <p className="text-3xl font-bold text-amber-600">{inProgress}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-5">
            <p className="text-xs text-slate-500 uppercase font-medium mb-1">Completed</p>
            <p className="text-3xl font-bold text-green-600">{completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6" data-testid="engagement-filter-tabs">
        {[
          { key: 'all', label: `All (${totalCases})` },
          { key: 'in_progress', label: `In Progress (${inProgress})` },
          { key: 'awaiting', label: `Awaiting Action (${awaitingAction})` },
          { key: 'completed', label: `Completed (${completed})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${filter === f.key ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
          >{f.label}</button>
        ))}
      </div>

      {/* Cases List */}
      {filteredCases.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No engagements found</p>
            <p className="text-slate-400 text-sm mt-1">Cases assigned to you will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCases.map(c => {
            const scope = partner.profession === 'CA' ? c.ca_scope : c.cs_scope;
            const tasks = scope?.tasks || [];
            const doneTasks = tasks.filter(t => t.status === 'completed').length;
            const ctx = c.lead_context || {};
            return (
              <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/r-pace/cases/${c.id}`)} data-testid={`partner-case-${c.id}`}>
                <CardContent className="p-4 md:p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{c.company_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {ctx.intent_label && <span className="text-[10px] text-slate-400">{ctx.intent_label}</span>}
                        {tasks.length > 0 && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                            <CheckCircle className="w-3 h-3" /> {doneTasks}/{tasks.length} tasks
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-600'}`}>
                      {STATUS_LABELS[c.status] || c.status}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
